import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/AddOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import LineItemsDialog from '@/components/common/LineItemsDialog';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import type { ClientDto } from '@/types/materials';
import type { CreateVasChargeRequest, GenerateInvoiceRequest, InvoiceDto, InvoiceLineDto, VasChargeDto } from '@/types/billing';

async function downloadInvoicePdf(invoice: InvoiceDto) {
  const response = await apiClient.get(`/billing/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoice.invoiceNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function InvoicesTab() {
  const { t } = useTranslation('billing');
  const queryClient = useQueryClient();

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => (await apiClient.get<InvoiceDto[]>('/billing/invoices')).data,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
  });

  const [genOpen, setGenOpen] = useState(false);
  const [viewing, setViewing] = useState<InvoiceDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<InvoiceDto | null>(null);
  const [clientId, setClientId] = useState('');
  const [periodStart, setPeriodStart] = useState(() => new Date().toISOString().slice(0, 8) + '01');
  const [periodEnd, setPeriodEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['invoices'] });

  const generateMutation = useMutation({
    mutationFn: () => {
      const payload: GenerateInvoiceRequest = { clientId, periodStart, periodEnd, notes: notes || null };
      return apiClient.post('/billing/invoices/generate', payload);
    },
    onSuccess: () => {
      invalidate();
      setGenOpen(false);
    },
  });

  const finalizeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/billing/invoices/${id}/finalize`),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/billing/invoices/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<InvoiceDto>[] = [
    { field: 'invoiceNumber', headerName: t('table.invoiceNumber') },
    { field: 'clientName', headerName: t('table.client'), render: (r) => `${r.clientCode} — ${r.clientName}` },
    {
      field: 'billingPeriodStart',
      headerName: t('table.billingPeriod'),
      render: (r) => `${new Date(r.billingPeriodStart).toLocaleDateString()} – ${new Date(r.billingPeriodEnd).toLocaleDateString()}`,
    },
    { field: 'gstType', headerName: t('table.gstType') },
    { field: 'totalAmount', headerName: t('table.totalAmount'), align: 'right', render: (r) => `₹${r.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Button size="small" onClick={() => setViewing(r)}>
            {t('view')}
          </Button>
          {r.pdfPath && (
            <Button size="small" startIcon={<DownloadIcon fontSize="small" />} onClick={() => void downloadInvoicePdf(r)}>
              {t('downloadPdf')}
            </Button>
          )}
          <IfPermitted permission={PERMISSIONS.BILLING_MANAGE}>
            {r.status === 'Draft' && (
              <Button size="small" onClick={() => finalizeMutation.mutate(r.id)} disabled={finalizeMutation.isPending}>
                {t('finalize')}
              </Button>
            )}
            {r.status === 'Draft' && (
              <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
                {t('delete')}
              </Button>
            )}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<InvoiceLineDto>[] = [
    { field: 'chargeCategory', headerName: t('form.chargeCategory') },
    { field: 'chargeCode', headerName: t('form.chargeCode') },
    { field: 'quantity', headerName: t('form.quantity') },
    { field: 'rate', headerName: t('form.rate') },
    { field: 'amount', headerName: t('form.amount') },
  ];

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <IfPermitted permission={PERMISSIONS.BILLING_MANAGE}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGenOpen(true)}>
            {t('generateInvoice')}
          </Button>
        </IfPermitted>
      </Stack>

      <DataTable columns={columns} rows={invoices} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="invoices" />

      <Dialog open={genOpen} onClose={() => setGenOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('generateInvoice')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('form.client')} value={clientId} onChange={setClientId} fullWidth
              options={clients.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                type="date" label={t('form.periodStart')} value={periodStart} fullWidth
                InputLabelProps={{ shrink: true }} onChange={(e) => setPeriodStart(e.target.value)}
              />
              <TextField
                type="date" label={t('form.periodEnd')} value={periodEnd} fullWidth
                InputLabelProps={{ shrink: true }} onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </Stack>
            <TextField label={t('form.notes')} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setGenOpen(false)}>{t('discardChanges')}</Button>
          <Button variant="contained" disabled={!clientId || generateMutation.isPending} onClick={() => generateMutation.mutate()}>
            {t('generateInvoice')}
          </Button>
        </DialogActions>
      </Dialog>

      <LineItemsDialog
        open={Boolean(viewing)}
        onClose={() => setViewing(null)}
        title={viewing?.invoiceNumber ?? ''}
        subtitle={viewing ? `${viewing.clientCode} — ${viewing.clientName} · ₹${viewing.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : undefined}
        columns={lineColumns}
        rows={viewing?.lines ?? []}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteInvoice.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}

function VasChargesTab() {
  const { t } = useTranslation('billing');
  const queryClient = useQueryClient();

  const { data: charges = [], isLoading } = useQuery({
    queryKey: ['vas-charges'],
    queryFn: async () => (await apiClient.get<VasChargeDto[]>('/billing/vas-charges')).data,
  });
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
  });

  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<VasChargeDto | null>(null);
  const [clientId, setClientId] = useState('');
  const [chargeDate, setChargeDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [chargeCode, setChargeCode] = useState('');
  const [description, setDescription] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [rate, setRate] = useState(0);
  const [gstRatePercent, setGstRatePercent] = useState(18);

  const resetForm = () => {
    setClientId('');
    setChargeDate(new Date().toISOString().slice(0, 10));
    setChargeCode('');
    setDescription('');
    setQuantity(1);
    setRate(0);
    setGstRatePercent(18);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['vas-charges'] });

  const createMutation = useMutation({
    mutationFn: () => {
      const payload: CreateVasChargeRequest = { clientId, chargeDate, chargeCode, description: description || null, quantity, rate, gstRatePercent };
      return apiClient.post('/billing/vas-charges', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/billing/vas-charges/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<VasChargeDto>[] = [
    { field: 'chargeDate', headerName: t('table.chargeDate'), render: (r) => new Date(r.chargeDate).toLocaleDateString() },
    { field: 'clientName', headerName: t('table.client') },
    { field: 'chargeCode', headerName: t('form.chargeCode') },
    { field: 'description', headerName: t('form.description'), render: (r) => r.description ?? '—' },
    { field: 'amount', headerName: t('form.amount'), align: 'right', render: (r) => `₹${r.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) =>
        r.status === 'Unbilled' ? (
          <IfPermitted permission={PERMISSIONS.BILLING_MANAGE}>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </IfPermitted>
        ) : null,
    },
  ];

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <IfPermitted permission={PERMISSIONS.BILLING_MANAGE}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
            {t('addVasCharge')}
          </Button>
        </IfPermitted>
      </Stack>

      <DataTable columns={columns} rows={charges} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="vas-charges" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('addVasCharge')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('form.client')} value={clientId} onChange={setClientId} fullWidth
              options={clients.map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` }))}
            />
            <TextField
              type="date" label={t('form.chargeDate')} value={chargeDate} fullWidth
              InputLabelProps={{ shrink: true }} onChange={(e) => setChargeDate(e.target.value)}
            />
            <TextField label={t('form.chargeCode')} value={chargeCode} onChange={(e) => setChargeCode(e.target.value)} fullWidth />
            <TextField label={t('form.description')} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={2} />
            <Stack direction="row" spacing={2}>
              <TextField type="number" label={t('form.quantity')} value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} fullWidth />
              <TextField type="number" label={t('form.rate')} value={rate} onChange={(e) => setRate(Number(e.target.value))} fullWidth />
              <TextField type="number" label={t('form.gstRatePercent')} value={gstRatePercent} onChange={(e) => setGstRatePercent(Number(e.target.value))} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('discardChanges')}</Button>
          <Button variant="contained" disabled={!clientId || !chargeCode || createMutation.isPending} onClick={() => createMutation.mutate()}>
            {t('addVasCharge')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteVasCharge.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}

export default function BillingPage() {
  const { t } = useTranslation('billing');
  const [tab, setTab] = useState(0);

  return (
    <>
      <PageHeader title={t('title')} subtitle={t('subtitle')} />
      <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={{ mb: 2 }}>
        <Tab label={t('tabs.invoices')} />
        <Tab label={t('tabs.vasCharges')} />
      </Tabs>
      <Box hidden={tab !== 0}>
        <InvoicesTab />
      </Box>
      <Box hidden={tab !== 1}>
        <VasChargesTab />
      </Box>
    </>
  );
}
